import React, { useEffect, useState, useRef } from 'react';
import { getBaseDir, apiCall, userHasRole } from '../../utils';
import moment from 'moment';
import Swal from 'sweetalert2';

// Custom hook para monitorar localStorage
const useLocalStorage = (key, initialValue) => {
    // Estado para armazenar o valor
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.error(error);
            return initialValue;
        }
    });

    // Função para atualizar o valor
    const setValue = value => {
        try {
            setStoredValue(value);
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(error);
        }
    };

    return [storedValue, setValue];
};

const BankReconciliationMovements = ({
    isLoading = false,
    updateHeadData,
    updateBodyData,
    bodyData,
    removeBodyData,
    fetchBodyData,
    mainFinancialAccountData
}) => {
    const [bankMovements, setBankMovements] = useState([]);
    const [headData] = useLocalStorage('bankReconciliationHeadData', null);
    const previousHeadDataRef = useRef(null);
    const apiCallXhr = useRef(null);
    const isAdminAssistantOrAdmin = userHasRole('ADMIN_ASSISTANT') || userHasRole('ADMIN');
    
    // Agrupar movimentações por data
    const groupedMovements = bankMovements.reduce((acc, movement) => {
        const date = moment(movement.DATA).format('DD/MM/YYYY');
        if (!acc[date]) {
            acc[date] = [];
        }
        acc[date].push(movement);
        return acc;
    }, {});

    //console.log('groupedMovements', groupedMovements);
    //console.log('bankMovements', bankMovements);

    const fetchMovements = () => {
        const headData = JSON.parse(localStorage.getItem('bankReconciliationHeadData'));
        if (!headData) return;

        // Comparar com o estado anterior
        const currentHeadDataString = JSON.stringify(headData);
        if (currentHeadDataString === previousHeadDataRef.current) {
            return; // Evitar requisição duplicada
        }

        // Atualizar a referência
        previousHeadDataRef.current = currentHeadDataString;

        $('.BankReconciliationTabsContent').block(window._loader.loading);

        if (apiCallXhr.current) {
            apiCallXhr.current.abort();
        }

        apiCallXhr.current = apiCall({
            method: 'GET',
            endpoint: 'api/v1/financial/bank_reconciliation/movements',
            data: {
                branchId: headData.branchId,
                financialAccountId: headData.financialAccount.id,
                startDate: headData.dateRange ? headData.dateRange.split('|')[0] : moment().startOf('month').format('YYYY-MM-DD'),
                endDate: headData.dateRange ? headData.dateRange.split('|')[1] : moment().endOf('day').format('YYYY-MM-DD')
            },
            successCallback: function(res) {
                setBankMovements(res.items || []);
                $('.BankReconciliationTabsContent').unblock();
                apiCallXhr.current = null;
            },
            errorCallback: function(res) {
                console.error(res);
                $('.BankReconciliationTabsContent').unblock();
                apiCallXhr.current = null;
            }
        });
    };

    // Efeito inicial para carregar os movimentos
    useEffect(() => {
        fetchMovements();
    }, []);

    // Monitorar mudanças no localStorage
    useEffect(() => {
        const handleStorageChange = () => {
            fetchMovements();
        };

        // Adicionar listener para mudanças no localStorage
        window.addEventListener('storage', handleStorageChange);

        // Criar um MutationObserver para monitorar mudanças no localStorage
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    fetchMovements();
                }
            });
        });

        // Observar o localStorage
        const target = document.documentElement;
        observer.observe(target, {
            childList: true,
            characterData: true,
            subtree: true
        });

        // Cleanup
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            observer.disconnect();
        };
    }, []);

    const isConciliated = (movement) => {
        //console.log('Movimento:', movement.ID);
        //console.log('Movimento:', movement);
        // Verifica se tem contas_a_pagar_ids
        if (movement.contas_a_pagar_ids) {
            //console.log('====================================');
            return true;
        }
    
        // Verifica se tem parcelas_ids ou recebimentos_ids
        if (movement.parcelas_ids || movement.recebimentos_ids) {
            //console.log('====================================');
            return true;
        }
    
        // Verifica se tem movimentacoes_detalhes
        if (movement.movimentacoes_detalhes) {
            try {
                // Adicionar logs para debug
                
                //console.log('Detalhes brutos:', movement.movimentacoes_detalhes);
                
                // Tentar fazer o parse dos detalhes
                const detalhes = JSON.parse('[' + movement.movimentacoes_detalhes + ']');
                //console.log('Detalhes parseados:', detalhes);
                
                
                // Verificar cada detalhe
                return detalhes.some(detalhe => {
                    ////console.log('Verificando detalhe:', detalhe);
                    return (
                        // Verificar se qualquer um dos IDs existe e não é nulo
                        (detalhe.conta_pagar_id && detalhe.conta_pagar_id !== null) || 
                        (detalhe.parcela_id && detalhe.parcela_id !== null) || 
                        (detalhe.recebimento_id && detalhe.recebimento_id !== null)
                    );
                });
            } catch (e) {
                console.error('Erro ao parsear movimentacoes_detalhes:', e);
                console.error('Conteúdo que causou erro:', movement.movimentacoes_detalhes);
                return false;
            }
        }
        //console.log('====================================');
    
        // Se nenhuma das condições acima for verdadeira, não está conciliado
        return false;
    };

    const deleteMovements = (movements, date, branchId) => {
        for (const movement of movements) {
            //console.log('movement', movement);
            if (isConciliated(movement)) {
                return Swal.fire({
                    title: 'Erro',
                    text: 'Não é possível deletar extrato com movimentações conciliadas.',
                    icon: 'error'
                });
            }
        }

        Swal.fire({
            title: 'Confirmar Exclusão',
            text: 'Tem certeza que deseja excluir estas movimentações?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, excluir',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                //console.log('Excluir movimentações');
                apiCall({
                    method: 'DELETE',
                    endpoint: 'api/v1/financial/bank_reconciliation/delete_movements',
                    data: {
                        branchId: branchId,
                        date: date
                    },
                    successCallback: function(res) {
                        //console.log('res', res);
                        if (res.error) {
                            window.toast({
                                error: true,
                                text: res.message
                            });
                        } else {
                            window.toast({
                                success: true,
                                text: 'Movimentações excluídas com sucesso.'
                            });
                            previousHeadDataRef.current = null;
                            fetchMovements();
                            fetchBodyData();
                        }
                    },
                    errorCallback: function(res) {
                        console.error('Erro ao excluir movimentações:', res);
                    }
                });
            }
        });
    }

    const formatValue = (value) => {
        const numValue = parseFloat(value);
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numValue);
    }

    React.useEffect(() => {
        return () => {
            for (const movement of bankMovements) {
                $(document).off(`click .unconciliate-btn-${movement.ID}`);
            }
        };
    }, []);

    //console.log('isAdminAssistantOrAdmin', isAdminAssistantOrAdmin);

    return (
        <div className="bank-statements">
            {Object.entries(groupedMovements).map(([date, movements]) => {
                const dayTotal = movements.reduce((sum, mov) => sum + parseFloat(mov.VALUE), 0);
                
                return (
                    <div key={date} className="card mb-4">
                        <div className="card-header bg-light">
                            <div className="d-flex justify-content-between w-100">
                                <h5 className="mb-0">{date}</h5>
                                <span className={`fw-bold ${dayTotal >= 0 ? 'text-success' : 'text-danger'}`}>
                                    {formatValue(dayTotal)}
                                </span>
                                {isAdminAssistantOrAdmin && (
                                    <button className="btn btn-sm btn-danger align-self-end" onClick={() => {
                                        const headData = JSON.parse(localStorage.getItem('bankReconciliationHeadData'));
                                        deleteMovements(movements, date, headData.branchId);
                                    }}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>ID</th>
                                            <th>Descrição</th>
                                            <th>Valor</th>
                                            <th>Tipo</th>
                                            <th>Status</th>
                                            <th>Detalhes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {movements.map(movement => (
                                            <tr key={movement.ID_TRANSACAO}>
                                                <td>{movement.ID}</td>
                                                <td>
                                                    <span className="text-truncate d-inline-block" style={{maxWidth: '400px'}}>
                                                        {movement.DESCRIPTION}
                                                    </span>
                                                </td>
                                                <td className={parseFloat(movement.VALUE) >= 0 ? 'text-success' : 'text-danger'}>
                                                    {formatValue(movement.VALUE)}
                                                </td>
                                                <td>
                                                    <span className={`badge ${movement.TIPO === 0 ? 'bg-success' : 'bg-danger'}`}>
                                                        {movement.TIPO === 0 ? 'Crédito' : 'Débito'}
                                                    </span>
                                                </td>
                                                <td>
                                                    {isConciliated(movement) ? (
                                                        <span className="badge bg-success">
                                                            <i className="fas fa-check-circle me-1"></i>
                                                            Conciliado
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-warning text-dark">
                                                            <i className="fas fa-clock me-1"></i>
                                                            Pendente
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {movement.movimentacoes_detalhes && (
                                                        <button 
                                                            className="btn btn-sm btn-info"
                                                            onClick={() => {
                                                                try {
                                                                    const detalhes = JSON.parse(movement.movimentacoes_detalhes);
                                                                    //console.log('movimentacoes_detalhes:', movement.movimentacoes_detalhes);
                                                                    //console.log('Detalhes da movimentação:', detalhes);
                                                                    
                                                                    let html = `
                                                                        <div class="table-responsive">
                                                                            <table class="table table-striped">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Tipo</th>
                                                                                        <th>ID</th>
                                                                                        <th>Descrição</th>
                                                                                        <th>Valor</th>
                                                                                        <th>Vencimento</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                    `;

                                                                    detalhes.forEach(detalhe => {
                                                                        let tipo = '';
                                                                        let id = '';
                                                                        
                                                                        if (detalhe.conta_pagar_id) {
                                                                            tipo = 'Conta a Pagar';
                                                                            id = detalhe.conta_pagar_id;
                                                                        } else if (detalhe.parcela_id) {
                                                                            tipo = 'Parcela';
                                                                            id = detalhe.parcela_id;
                                                                        } else if (detalhe.recebimento_id) {
                                                                            tipo = 'Recebimento';
                                                                            id = detalhe.recebimento_id;
                                                                        }

                                                                        html += `
                                                                            <tr>
                                                                                <td>${tipo}</td>
                                                                                <td>${id}</td>
                                                                                <td>${detalhe.descricao}</td>
                                                                                <td>${detalhe.valor ? `R$ ${parseFloat(detalhe.valor).toFixed(2)}` : '-'}</td>
                                                                                <td>${detalhe.data_vencimento ? moment(detalhe.data_vencimento).format('DD/MM/YYYY') : '-'}</td>
                                                                            </tr>
                                                                        `;
                                                                    });

                                                                    html += `
                                                                                </tbody>
                                                                            </table>

                                                                            <button 
                                                                                class="btn btn-sm btn-danger unconciliate-btn-${movement.ID}"
                                                                                data-movimento-id="${movement.ID}"
                                                                                data-detalhes="${movement.movimentacoes_detalhes}"
                                                                            >
                                                                                <i class="fas fa-unlink"></i> Desconciliar
                                                                            </button>
                                                                        </div>
                                                                    `;

                                                                    // Mostrar o modal com os detalhes
                                                                    $('#modal-bank-reconciliation-view-movement-details').modal('show');
                                                                    $('#modal-bank-reconciliation-view-movement-details').find('.modal-body').html(html);

                                                                    // Adicionar evento para desconciliar
                                                                    $(`.unconciliate-btn-${movement.ID}`).on('click', function() {
                                                                        const movimentoId = $(this).data('movimento-id');

                                                                        Swal.fire({
                                                                            title: 'Confirmar Desconciliação',
                                                                            text: 'Tem certeza que deseja desconciliar este item?',
                                                                            icon: 'warning',
                                                                            showCancelButton: true,
                                                                            confirmButtonColor: '#d33',
                                                                            cancelButtonColor: '#3085d6',
                                                                            confirmButtonText: 'Sim, desconciliar',
                                                                            cancelButtonText: 'Cancelar'
                                                                        }).then((result) => {
                                                                            if (result.isConfirmed) {
                                                                                apiCall({
                                                                                    method: 'POST',
                                                                                    endpoint: 'api/v1/financial/bank_reconciliation/unconciliate',
                                                                                    data: {
                                                                                        movimento_id: movimentoId
                                                                                    },
                                                                                    successCallback: function(response) {
                                                                                        if (!response.error) {
                                                                                            Swal.fire(
                                                                                                'Sucesso!',
                                                                                                'Item desconciliado com sucesso.',
                                                                                                'success'
                                                                                            );
                                                                                            
                                                                                            // Fechar o modal
                                                                                            $('#modal-bank-reconciliation-view-movement-details').modal('hide');
                                                                                            
                                                                                            // Atualizar a lista de movimentações
                                                                                            previousHeadDataRef.current = null;
                                                                                            fetchMovements();
                                                                                            fetchBodyData();
                                                                                        } else {
                                                                                            Swal.fire(
                                                                                                'Erro!',
                                                                                                response.message || 'Erro ao desconciliar item.',
                                                                                                'error'
                                                                                            );
                                                                                        }
                                                                                    },
                                                                                    errorCallback: function(error) {
                                                                                        console.error('Erro ao desconciliar:', error);
                                                                                        Swal.fire(
                                                                                            'Erro!',
                                                                                            'Erro ao desconciliar item.',
                                                                                            'error'
                                                                                        );
                                                                                    }
                                                                                });
                                                                            }
                                                                        });
                                                                    });

                                                                } catch (e) {
                                                                    console.error('Erro ao parsear detalhes:', e);
                                                                    Swal.fire(
                                                                        'Erro!',
                                                                        'Erro ao carregar detalhes da movimentação.',
                                                                        'error'
                                                                    );
                                                                }
                                                            }}
                                                        >
                                                            <i className="fas fa-info-circle"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
            })}
            
            {bankMovements.length === 0 && (
                <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Nenhuma movimentação encontrada no período selecionado.
                </div>
            )}
        </div>
    );
}
    
export default BankReconciliationMovements;